// Dictionary, 855 part 2 — the PO1 loop's own acknowledgment segment: ACK
// (Line Item Acknowledgment). Generic X12 855 (5010) definitions only.
//
// PO1 itself, and everything else that recurs inside its loop and sub-loop
// (PID, MEA, PKG, PO3, CTP, REF, PER, SAC, SDQ, SCH, DTM, N1-N4) is a segment
// X12 defines once and reuses across transaction sets, so it's already
// defined in dictionary/header-segments.ts and dictionary/item-loop-
// segments.ts and merged in by index.ts, so it is not redefined here.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {LINE_ITEM_STATUS_CODES} from './line-item-status-codes';
import {UNIT_OR_BASIS_FOR_MEASUREMENT_CODES} from './unit-or-basis-for-measurement-codes';
import {DATE_TIME_QUALIFIER_CODES} from './date-time-qualifiers';
import {PRODUCT_SERVICE_ID_QUALIFIER_CODES} from './product-service-id-qualifiers';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const ACK_SEGMENTS_855: Record<string, SegmentDefinition> = {
  ACK: {
    id: 'ACK',
    name: 'Line Item Acknowledgment',
    purpose:
      "Reports the seller's disposition of one PO1 line — accepted, backordered, changed, rejected, etc. — along with the quantity and date that disposition applies to. Every PO1 line in an 855 carries at least one ACK.",
    loop: LoopId.Item,
    required: true,
    elements: [
      el(
        1,
        'Line Item Status Code',
        "This line's acknowledgment disposition.",
        {
          dataType: 'code',
          required: true,
          codes: LINE_ITEM_STATUS_CODES,
        }
      ),
      el(2, 'Quantity', 'The quantity this disposition applies to.', {
        dataType: 'numeric',
      }),
      el(
        3,
        'Unit or Basis for Measurement Code',
        'The unit the quantity is expressed in.',
        {
          dataType: 'code',
          codes: UNIT_OR_BASIS_FOR_MEASUREMENT_CODES,
        }
      ),
      el(4, 'Date/Time Qualifier', 'What the date in element 5 represents.', {
        dataType: 'code',
        codes: DATE_TIME_QUALIFIER_CODES,
      }),
      el(5, 'Date', 'The date the qualifier in element 4 describes.', {
        dataType: 'date',
        format: 'CCYYMMDD',
      }),
      el(
        6,
        'Request Reference Number',
        "Ties this acknowledgment back to the buyer's own request/quote reference, when used."
      ),
      el(
        7,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows.',
        {
          dataType: 'code',
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(
        8,
        'Product/Service ID',
        'The item identifier, in the scheme named by element 7.'
      ),
      el(
        9,
        'Product/Service ID Qualifier',
        'A second item identifier scheme, when the item carries more than one id.',
        {
          dataType: 'code',
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(10, 'Product/Service ID', 'The second item identifier.'),
      el(
        11,
        'Product/Service ID Qualifier',
        'A third item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(12, 'Product/Service ID', 'The third item identifier.'),
    ],
  },
};
